import { z } from "zod";
import { CURRENCIES } from "../status.js";
import {
  currencySchema,
  dateOnlySchema,
  nullableText,
  nonNegativeAmountSchema,
  versionSchema,
} from "./common.js";

export const EXPENSE_STATUSES = ["draft", "submitted", "paid"] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];

export const EXPENSE_ITEM_TYPES = [
  "mileage",
  "meal",
  "lodging",
  "supplies",
  "other",
] as const;
export type ExpenseItemType = (typeof EXPENSE_ITEM_TYPES)[number];

const percentageSchema = z
  .union([z.string(), z.number()])
  .pipe(z.coerce.number().finite().min(0).max(100));
const kilometersSchema = z
  .union([z.string(), z.number()])
  .pipe(z.coerce.number().finite().min(0).max(1_000_000));

export const expenseItemSchema = z
  .object({
    date: dateOnlySchema,
    type: z.enum(EXPENSE_ITEM_TYPES),
    description: z.string().trim().min(1).max(1000),
    amount: nonNegativeAmountSchema,
    currency: currencySchema.default("CHF"),
    kilometers: kilometersSchema.nullable().optional(),
    taxRate: percentageSchema.nullable().optional(),
    taxAmount: nonNegativeAmountSchema.nullable().optional(),
    taxCode: nullableText(80),
    projectId: z.string().uuid().nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.type === "mileage" &&
      (!value.kilometers || value.kilometers <= 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["kilometers"],
        message: "Mileage expenses require kilometers",
      });
    }
  });
export type ExpenseItemDto = z.infer<typeof expenseItemSchema>;

export const expenseClaimCreateSchema = z
  .object({
    currency: z.enum(CURRENCIES).default("CHF"),
    items: z.array(expenseItemSchema).min(1).max(500),
    notes: nullableText(4000),
  })
  .strict();
export type ExpenseClaimCreateDto = z.infer<typeof expenseClaimCreateSchema>;
export const expenseCreateSchema = expenseClaimCreateSchema;

export const expenseClaimUpdateSchema = expenseClaimCreateSchema
  .partial()
  .extend({ version: versionSchema })
  .strict();
export type ExpenseClaimUpdateDto = z.infer<typeof expenseClaimUpdateSchema>;

export const expenseClaimSubmitSchema = z
  .object({ version: versionSchema })
  .strict();
export type ExpenseClaimSubmitDto = z.infer<typeof expenseClaimSubmitSchema>;

export const expenseClaimPaySchema = z
  .object({
    version: versionSchema,
    paidAt: z.string().datetime().optional(),
    note: nullableText(2000),
  })
  .strict();
export type ExpenseClaimPayDto = z.infer<typeof expenseClaimPaySchema>;

/** A payer sends a submitted claim back to the employee for correction. */
export const expenseClaimReturnSchema = z
  .object({
    version: versionSchema,
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type ExpenseClaimReturnDto = z.infer<typeof expenseClaimReturnSchema>;

/** AI may suggest values, but the suggestion is always reviewed manually. */
export const expenseAiSuggestionSchema = z
  .object({
    date: dateOnlySchema.nullable(),
    type: z.enum(EXPENSE_ITEM_TYPES).nullable(),
    description: z.string().trim().max(1000).nullable(),
    amount: nonNegativeAmountSchema.nullable(),
    currency: currencySchema.nullable(),
    kilometers: kilometersSchema.nullable(),
    taxRate: percentageSchema.nullable(),
    confidence: z.number().finite().min(0).max(1),
  })
  .strict();
export type ExpenseAiSuggestionDto = z.infer<typeof expenseAiSuggestionSchema>;
